'use client';

import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useLatestRef } from '../../hooks/useLatestRef';
import { useSlots } from '../../hooks/useSlots';
import { cn } from '../../lib/utils';
import { DropzoneRemoveConfirmSlot } from './DropzoneRemoveConfirm';
import { useAnnouncementResolvers, useFilePreview, useFileProcessing, usePasteUpload, useValidationState } from './hooks';
import {
  DropzoneEmptyStateSlot,
  DropzoneFileListSlot,
  DropzonePreviewSlot,
  DropzoneProgressSlot,
  DropzoneTriggerSlot,
  DropzoneValidationMessageSlot,
  dropzoneRootVariants,
} from './slots';
import type { DropzoneProps, DropzoneRef } from './types';
import { buildAccept, isImageFile, isPreviewShowingError } from './utils';

const DropzoneBase = forwardRef<DropzoneRef, DropzoneProps>(
  (
    {
      files = [],
      initialFiles = [],
      onInitialFileRemove,
      onInitialFileDownload,
      status = 'default',
      size = 'md',
      accept,
      maxSize,
      maxFiles,
      multiple = false,
      noPaste = false,
      validator,
      onDrop,
      onRemove,
      onDownload,
      text,
      disabled = false,
      isDragActive: isDragActiveProp,
      isDragAccept: isDragAcceptProp,
      isDragReject: isDragRejectProp,
      fileProcessing,
      name,
      required,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedByProp,
      className,
      children,
      ...rootProps
    },
    ref,
  ) => {
    const descriptionId = useId();
    const labelId = useId();
    const emptyStateId = useId();
    const [internalRejections, setInternalRejections] = useState<FileRejection[]>([]);
    const [statusMessage, setStatusMessage] = useState<string>('');
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const resolvedAccept = accept ? buildAccept(accept) : undefined;

    const filesRef = useLatestRef(files);
    const onDropRef = useLatestRef(onDrop);

    const queueAnnouncement = useCallback((text: string) => {
      setStatusMessage(text);
    }, []);

    const announce = useAnnouncementResolvers(text?.announcements);

    const {
      processingStates,
      isProcessing,
      processFiles: startFileProcessing,
      clearProcessingState,
      aggregateProgress,
    } = useFileProcessing(
      fileProcessing
        ? {
            ...fileProcessing,
            onStart: (startedFiles) => {
              fileProcessing.onStart?.(startedFiles);
              const singleFileName = startedFiles.length === 1 ? startedFiles[0]?.name : undefined;
              queueAnnouncement(announce.processing(startedFiles.length, singleFileName));
            },
            onFileComplete: (file, state) => {
              fileProcessing.onFileComplete?.(file, state);
              if (state.status === 'error' && state.error) {
                const errorTitle = text?.emptyState?.errorTitle ?? text?.preview?.errorTitle;
                const message = errorTitle ? `${errorTitle}. ${state.error.message}` : state.error.message;
                queueAnnouncement(announce.processingFailed(1, 1, message, file.name));
              }
            },
            onComplete: (results) => {
              fileProcessing.onComplete?.(results);
              const successFiles = [...results.entries()].filter(([, s]) => s.status === 'complete');
              const successCount = successFiles.length;

              if (successCount > 0) {
                const singleFileName = successCount === 1 ? successFiles[0]?.[0].name : undefined;
                queueAnnouncement(announce.processed(successCount, singleFileName));
              }
            },
          }
        : undefined,
    );

    useEffect(() => {
      if (!hiddenInputRef.current || !name) return;

      const dataTransfer = new DataTransfer();
      files.forEach((file) => {
        dataTransfer.items.add(file);
      });
      hiddenInputRef.current.files = dataTransfer.files;
    }, [files, name]);

    useEffect(() => {
      const currentFiles = new Set(files);
      const staleProcessingFiles: File[] = [];

      for (const file of processingStates.keys()) {
        if (!currentFiles.has(file)) {
          staleProcessingFiles.push(file);
        }
      }

      if (staleProcessingFiles.length > 0) {
        clearProcessingState(staleProcessingFiles);
      }
    }, [files, processingStates, clearProcessingState]);

    const handleDragEnter = useCallback(() => {
      setInternalRejections([]);
    }, []);

    const processFiles = useCallback(
      (acceptedFiles: File[], rejections: FileRejection[]) => {
        setInternalRejections(rejections);

        const firstRejection = rejections[0];
        const firstError = firstRejection?.errors[0];
        const errorMessage = firstError?.message ?? 'Invalid file';

        if (acceptedFiles.length > 0 && rejections.length === 0) {
          if (!fileProcessing?.process) {
            const singleFileName = acceptedFiles.length === 1 ? acceptedFiles[0]?.name : undefined;
            queueAnnouncement(announce.added(acceptedFiles.length, singleFileName));
          }
        } else if (acceptedFiles.length === 0 && rejections.length > 0) {
          const singleFileName = rejections.length === 1 ? firstRejection?.file.name : undefined;
          queueAnnouncement(announce.rejected(rejections.length, errorMessage, singleFileName));
        } else if (acceptedFiles.length > 0 && rejections.length > 0) {
          queueAnnouncement(announce.mixed(acceptedFiles.length, rejections.length, errorMessage));
        }

        if (acceptedFiles.length > 0) {
          onDropRef.current?.(acceptedFiles, rejections);

          if (fileProcessing?.process) {
            startFileProcessing(acceptedFiles);
          }
        }
      },
      [fileProcessing?.process, announce.added, announce.rejected, announce.mixed, startFileProcessing, queueAnnouncement],
    );

    usePasteUpload({
      disabled,
      noPaste,
      accept: resolvedAccept,
      maxSize,
      maxFiles,
      multiple,
      filesRef,
      wrapperRef,
      onPaste: processFiles,
    });

    const isEmpty = files.length === 0 && initialFiles.length === 0;

    const {
      getRootProps,
      getInputProps,
      rootRef,
      isDragActive: isDragActiveInternal,
      isDragAccept: isDragAcceptInternal,
      isDragReject: isDragRejectInternal,
      open,
    } = useDropzone({
      accept: resolvedAccept,
      maxSize,
      maxFiles,
      multiple,
      validator,
      disabled,
      noClick: !isEmpty,
      noKeyboard: !isEmpty,
      onDrop: (acceptedFiles, fileRejections) => {
        processFiles(acceptedFiles, fileRejections);
      },
      onDragEnter: handleDragEnter,
    });

    const isDragActive = isDragActiveProp ?? isDragActiveInternal;
    const isDragAccept = isDragAcceptProp ?? isDragAcceptInternal;
    const isDragReject = isDragRejectProp ?? isDragRejectInternal;

    useImperativeHandle(ref, () => ({
      focus: () => rootRef.current?.focus(),
    }));

    const firstFile = files[0] ?? null;
    const firstFileProcessing = firstFile ? processingStates.get(firstFile) : undefined;
    const firstFilePreviewUrl = useFilePreview(firstFile);

    const previewHandlesError = isPreviewShowingError(files, firstFileProcessing);

    const { effectiveStatus, validationMessage, dragAnnouncement } = useValidationState({
      status,
      internalRejections,
      processingStates,
      files,
      previewHandlesError,
      isDragActive,
      isDragAccept,
      isDragReject,
      isEmpty,
      text,
      maxSize,
    });

    const handleRemoveFile = useCallback(
      (fileToRemove: File) => {
        onRemove?.(fileToRemove);

        rootRef.current?.focus();
      },
      [onRemove],
    );

    const firstExistingFile = initialFiles[0];
    const previewSrc = files.length === 0 && firstExistingFile?.src && isImageFile(firstExistingFile) ? firstExistingFile.src : undefined;

    const totalFileCount = files.length > 0 ? files.length : initialFiles.length;

    const { slots } = useSlots(children, {
      emptyState: {
        Component: DropzoneEmptyStateSlot,
        props: {
          id: emptyStateId,
          isDragActive,
          isDragAccept,
          isDragReject,
          multiple,
          disabled,
          status: effectiveStatus,
          text: text?.emptyState,
        },
      },
      preview: {
        Component: DropzonePreviewSlot,
        props: {
          file: firstFile,
          previewUrl: firstFilePreviewUrl,
          src: previewSrc,
          processingState: firstFileProcessing,
          fileCount: totalFileCount,
          size,
          text: text?.preview,
          initialFileTitle: firstExistingFile?.title,
        },
      },
      fileList: {
        Component: DropzoneFileListSlot,
        props: {
          files,
          initialFiles: multiple || files.length === 0 ? initialFiles : [],
          onRemove: onRemove ? handleRemoveFile : undefined,
          onInitialFileRemove,
          onInitialFileDownload,
          onDownload,
          processingStates: fileProcessing ? processingStates : undefined,
          disabled,
          dropzoneRef: rootRef,
          text: text?.fileList,
        },
      },
      trigger: {
        Component: DropzoneTriggerSlot,
        props: { disabled, open, isEmpty, multiple, dropzoneLabelId: ariaLabelledBy || labelId, text: text?.trigger },
      },
      validationMessage: {
        Component: DropzoneValidationMessageSlot,
        props: { children: validationMessage },
      },
      progress: {
        Component: DropzoneProgressSlot,
        props: { value: aggregateProgress, isProcessing, text: text?.progress },
      },
    });

    const baseAriaLabel = text?.ariaLabel ?? 'File upload';
    const fileCount = files.length + initialFiles.length;
    const ariaLabel = isEmpty ? baseAriaLabel : `${baseAriaLabel}, ${fileCount} ${fileCount === 1 ? 'file' : 'files'}`;
    const ariaDescription =
      text?.ariaDescription ?? (isEmpty ? 'Drag and drop files here, or press Enter to browse' : 'Drag and drop to add files');
    const showInternalDescription = !isEmpty && !ariaDescribedByProp;

    const ariaDescribedByIds =
      [
        ariaDescribedByProp,
        isEmpty ? `${emptyStateId}-title` : null,
        isEmpty ? `${emptyStateId}-description` : null,
        isEmpty && text?.emptyState?.helperText ? `${emptyStateId}-helper-text` : null,
        showInternalDescription ? descriptionId : null,
      ]
        .filter(Boolean)
        .join(' ') || undefined;

    const inputProps = getInputProps();

    return (
      <div data-spellbook-name="dropzone" ref={wrapperRef} className="dropzone-wrapper">
        <input {...inputProps} aria-label={ariaLabel} />

        <div
          {...getRootProps({
            ...rootProps,
            className: cn(
              dropzoneRootVariants({
                size,
                status: effectiveStatus,
                disabled,
                isEmpty,
                isDragAccept: isDragActive && isDragAccept,
                isDragReject: isDragActive && isDragReject,
              }),
              className,
            ),
          })}
          role={isEmpty ? 'button' : 'region'}
          tabIndex={disabled ? -1 : 0}
          aria-labelledby={ariaLabelledBy}
          aria-label={!ariaLabelledBy ? ariaLabel : undefined}
          aria-describedby={ariaDescribedByIds}
          aria-disabled={disabled || undefined}
          aria-invalid={effectiveStatus === 'error' || undefined}
          aria-busy={isProcessing}
        >
          {name && (
            <input
              ref={hiddenInputRef}
              type="file"
              name={name}
              required={required}
              multiple={multiple}
              tabIndex={-1}
              aria-hidden="true"
              className="sr-only"
            />
          )}

          {showInternalDescription && (
            <span id={descriptionId} className="sr-only">
              {ariaDescription}
            </span>
          )}

          {isEmpty && slots.emptyState}
          {slots.preview}
          {!isEmpty && slots.fileList}
        </div>
        {!ariaLabelledBy && (
          <span id={labelId} className="sr-only">
            {ariaLabel}
          </span>
        )}
        {slots.validationMessage}
        {slots.trigger}

        <output aria-atomic="true" className="sr-only">
          {statusMessage || dragAnnouncement}
        </output>
      </div>
    );
  },
);
DropzoneBase.displayName = 'DropzoneBase';

/**
 * A file upload component that lets users drag-and-drop, click to browse, or paste files.
 * Supports single or multiple file uploads with validation and preview capabilities.
 *
 * ### When to use
 * - **Single file**: Profile photos, avatars, document uploads, logo uploads
 * - **Multiple files**: Image galleries, batch document uploads, attachment lists
 * - **With validation**: When file type, size, or count restrictions apply
 * - **With server validation**: Content moderation, file scanning, or async checks
 *
 * ### When NOT to use
 * - For simple file inputs without drag-and-drop: use a native `<input type="file">` or a `Button` with a hidden input
 * - For displaying already-uploaded files without upload functionality: use `FileItem` directly
 *
 * ### Upload methods
 * - **Drag and drop**: Drag files onto the dropzone
 * - **Click/keyboard**: Click or press Enter to open file picker
 * - **Paste**: Paste images from clipboard (Ctrl/Cmd+V) when dropzone is focused
 *
 * ### API
 * - `files`: Array of `File` objects currently selected
 * - `accept`: Accepted file types as extensions (e.g., `['.pdf', '.jpg']`), MIME types (e.g., `['application/pdf']`), or wildcards (e.g., `['image/*']`)
 * - `multiple`: Allow multiple file selection (default `false`)
 * - `maxSize`: Maximum file size in bytes
 * - `maxFiles`: Maximum number of files (only applies when `multiple={true}`)
 * - `size`: Visual size variant: `sm`, `md` (default), `lg`
 * - `status`: Visual status: `default` or `error`
 * - `disabled`: Disable all interactions
 * - `noPaste`: Disable paste-to-upload from clipboard
 * - `validator`: Custom sync validator returning `null` or `FileError`
 * - `fileProcessing`: Async processing pipeline (validation, upload) with progress and concurrency control
 * - `onDrop`: Called with accepted files after sync validation
 * - `onRemove`: Called when a file is removed from the list
 * - `onDownload`: Called when a user downloads a newly dropped file (analytics hook; blob download is automatic)
 * - `initialFiles`: Pre-existing uploaded files to display. Shows in Preview and FileList. Hidden when new files are dropped.
 * - `onInitialFileRemove`: Called when a pre-existing initial file is removed
 * - `onInitialFileDownload`: Called when download is requested for an initial file. Consumer controls how downloads work (e.g., open URL, fetch and save). If not provided, the download button is hidden for initial files.
 *
 * ### Composition (slots)
 * Include slots as children only when customizing — all are rendered automatically:
 * - `<Dropzone.EmptyState>`: Upload prompt when no files selected. Rendered automatically when `files` is empty.
 * - `<Dropzone.Preview>`: Image preview for single file uploads. Rendered automatically for single image files.
 * - `<Dropzone.FileList>`: List of files with remove/download actions. Rendered automatically when files are present.
 * - `<Dropzone.RemoveConfirm>`: Confirmation modal for file removal. Used inside FileList.
 * - `<Dropzone.Progress>`: Processing progress indicator. Rendered automatically during `fileProcessing`.
 * - `<Dropzone.Trigger>`: "Choose file" button. Rendered automatically below the file list.
 * - `<Dropzone.ValidationMessage>`: Error message display. Rendered automatically when validation fails.
 *
 * ### Visual states
 * - **Empty**: Shows upload prompt with optional helper text for file requirements
 * - **Drag over (valid)**: Primary border indicates file will be accepted
 * - **Drag over (invalid)**: Error border with "File not allowed" message
 * - **With files**: Shows file list and/or image preview
 * - **Processing**: Loading/progress states during validation and upload, sets `aria-busy="true"`
 * - **Error**: Red border with validation message below
 *
 * ### Accessibility
 * - Dropzone is focusable and activated with Enter or Space
 * - Screen reader announcements for file additions, rejections, and processing states
 * - Drag state changes are announced via a live region
 * - File removal confirmation is keyboard-navigable
 * - All actions have accessible labels via tooltips or `aria-label`
 *
 * @see {@link https://react-dropzone.js.org/ | react-dropzone documentation}
 */
export const Dropzone = Object.assign(DropzoneBase, {
  EmptyState: DropzoneEmptyStateSlot,
  Preview: DropzonePreviewSlot,
  FileList: DropzoneFileListSlot,
  Progress: DropzoneProgressSlot,
  RemoveConfirm: DropzoneRemoveConfirmSlot,
  Trigger: DropzoneTriggerSlot,
  ValidationMessage: DropzoneValidationMessageSlot,
});
